import type { ReactNode } from "react";
import CircularProgress from "../../../basic/circularProgress/CircularProgress";
import ExerciseNumberBadge from "../../../basic/exerciseNumberBadge/ExerciseNumberBadge";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";

interface ExerciseCardProps {
    number: number;
    title: string;
    progress?: number; // 0-100, ignored when isResult
    pointsLabel?: string;
    isResult?: boolean; // showing no progress
    onClick?: () => void;
    children?: ReactNode;
    "data-testid"?: string;
}

const ExerciseCard = (props: ExerciseCardProps) => {
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();

    // A result keeps the label inside the card below MD, on its own line under the task.
    const showPointsLabelBelowTask = Boolean(props.pointsLabel && props.children && props.isResult && !isAboveMD);
    const showPointsLabelInline = Boolean(props.pointsLabel && (!props.children || isAboveMD));

    const Tag = props.onClick ? "button" : "div";

    return (
        <Tag
            className={`flex flex-col gap-s border border-p6-grey-lightest bg-white w-full text-left ${
                props.isResult ? "rounded-card" : "rounded-medium"
            } ${props.onClick ? "cursor-pointer" : ""} ${getStyleBasedOnBreakpoint(Breakpoint.MD, "p-m", "p-s")}`}
            onClick={props.onClick}
            data-testid={props["data-testid"]}
        >
            <div
                className={`flex flex-row gap-s ${
                    props.children
                        ? "items-start"
                        : getStyleBasedOnBreakpoint(Breakpoint.MD, "items-start", "items-center")
                }`}
            >
                {props.isResult ? (
                    <ExerciseNumberBadge number={props.number} />
                ) : (
                    <CircularProgress
                        number={props.number}
                        progress={props.progress ?? 0}
                    />
                )}
                <p
                    className={`flex-1 text-l text-p6-grey-dark ${
                        props.children
                            ? "min-h-xl leading-m flex items-center"
                            : getStyleBasedOnBreakpoint(Breakpoint.MD, "h-xl leading-xl line-clamp-1", "line-clamp-2")
                    }`}
                >
                    {props.title}
                </p>
                {showPointsLabelInline && (
                    <p className="flex shrink-0 text-l text-p6-grey-dark min-h-xl items-center">{props.pointsLabel}</p>
                )}
            </div>
            {showPointsLabelBelowTask && <p className="self-end text-l text-p6-grey-dark">{props.pointsLabel}</p>}
            {props.children}
        </Tag>
    );
};

export default ExerciseCard;
